import { useTranslation } from "react-i18next";
import { ExternalLink } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import { Badge } from "@/shared/components/ui/badge";
import { cn } from "@/infrastructure/lib/utils";
import type { SkillCatalogDetail } from "../types/skill";

/**
 * De onde a skill veio e quem responde por ela (AB#85634, AC32 e AC39).
 *
 * As três coisas moram juntas porque respondem à mesma pergunta — a procedência
 * — e são decididas no mesmo lugar, a importação:
 *
 *   - AUTOR: quem escreveu, declarado no `SKILL.md`. Não muda de mão, e um
 *     pacote de terceiro tem o nome de terceiro. Sem avatar: quem escreveu
 *     costuma não ter conta aqui, e o rosto errado ao lado do nome certo era
 *     exatamente a confusão que o AC32 desfez.
 *   - DONO: quem responde pela skill dentro do Prodgy. Esse tem rosto, porque é
 *     gente da casa.
 *   - FONTE: o endereço de onde o pacote veio, como LINK — o campo existe para
 *     se poder ir até lá conferir se saiu versão nova.
 *
 * Componente único porque as telas que leem uma skill são três — administração
 * do catálogo, configuração da instalação e o assistente — e a procedência
 * escrita três vezes divergiria na primeira correção.
 */
export const SkillProvenance = ({
  detail,
  className,
}: {
  detail: Pick<SkillCatalogDetail, "author" | "owner_profile" | "source_url">;
  className?: string;
}) => {
  const { t } = useTranslation();

  const dono = detail.owner_profile;
  // Sem nada a dizer, a linha não existe: uma fileira de "—" ocupa espaço para
  // informar ausência, que é o que a ausência já informa.
  if (!detail.author && !dono && !detail.source_url) return null;

  return (
    <div className={cn("flex flex-wrap items-center gap-x-4 gap-y-1 text-xs", className)}>
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <span className="font-medium">{t("skills.publish.author")}:</span>
        {detail.author ?? t("skills.publish.authorUndeclared")}
      </span>

      {dono && (
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <span className="font-medium">{t("skills.publish.ownerField")}:</span>
          <Avatar className="h-4 w-4">
            <AvatarImage src={dono.avatar_url || undefined} alt={dono.name ?? ""} />
            <AvatarFallback className="text-[9px]">
              {dono.name?.[0]?.toUpperCase() ?? "?"}
            </AvatarFallback>
          </Avatar>
          {dono.name}
        </span>
      )}

      {detail.source_url && (
        <a
          href={detail.source_url}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex"
        >
          <Badge variant="outline" className="gap-1 font-normal hover:bg-accent">
            <ExternalLink className="h-3 w-3" />
            {t("skills.publish.sourceLink")}
          </Badge>
        </a>
      )}
    </div>
  );
};
